// A class to track the state of markdown parsing
export class MarkdownStreamParser {
    private incompleteMarkdown = ''; // Store text with incomplete markers
    private openTags: Map<string, number> = new Map(); // Track open tags with their counts
    
    // Markdown markers and their tag representations
    private markdownMarkers: Record<string, { open: string, close: string }> = {
      '**': { open: '<span class="font-bold">', close: '</span>' },
      '*': { open: '<span class="italic">', close: '</span>' },
      '***': { open: '<span class="font-bold italic">', close: '</span>' },
      '__': { open: '<span class="underline">', close: '</span>' },
      '`': { open: '<code>', close: '</code>' },
      '#': { open: '<span class="font-bold text-2xl">', close: '</span>' },
      '##': { open: '<span class="font-bold text-xl">', close: '</span>' },
      '###': { open: '<span class="font-bold text-lg">', close: '</span>' },
    };
    
    // Regex patterns for markdown with capture groups
    private regexPatterns = {
      bold: /\*\*(.*?)\*\*/g,
      italic: /\*(?!\*)(.*?)\*/g,
      boldItalic: /\*\*\*(.*?)\*\*\*/g,
      underline: /__(.*?)__/g,
      inlineCode: /`(.*?)`/g,
      link: /\[([^\]]*)\]\((.*?)\)/g,
      heading1: /#\s+(.*?)(?:\n|$)/g,
      heading2: /##\s+(.*?)(?:\n|$)/g,
      heading3: /###\s+(.*?)(?:\n|$)/g,
    };
    
    constructor() {
      // Initialize open tags map with zero counts
      for (const marker of Object.keys(this.markdownMarkers)) {
        this.openTags.set(marker, 0);
      }
      // Add special case for links
      this.openTags.set('[', 0);
    }
    
    // Resets the parser state
    public reset(): void {
      this.incompleteMarkdown = '';
      for (const marker of this.openTags.keys()) {
        this.openTags.set(marker, 0);
      }
    }

    // Process a chunk of markdown text
    public processChunk(chunk: string): string {
      // If we have incomplete markdown from a previous chunk, combine them
      const textToProcess = this.incompleteMarkdown + chunk;
      
      // Check for tag completeness in the combined text
      if (this.hasIncompleteMarkers(textToProcess)) {
        // Save the combined text and wait for more chunks
        this.incompleteMarkdown = textToProcess;
        return '';
      }
      
      // If all tags are properly matched, format the text
      const formattedText = this.applyFormatting(textToProcess);
      
      // Reset the incomplete markdown since we've processed everything
      this.incompleteMarkdown = '';
      
      return formattedText;
    }
    
    // Get any remaining incomplete markdown after all chunks processed
    public getRemaining(): string {
      if (this.incompleteMarkdown) {
        // Try to auto-close any unclosed tags
        return this.closeIncompleteMarkdown(this.incompleteMarkdown);
      }
      return '';
    }

    // Auto-close any incomplete markdown tags
    private closeIncompleteMarkdown(text: string): string {
      // If nothing to fix, just return the text as-is
      if (!this.hasIncompleteMarkers(text)) {
        return this.applyFormatting(text);
      }
      
      // Make a copy of the current state
      let modifiedText = text;
      
      // Try to close all open markers
      for (const [marker, count] of this.openTags.entries()) {
        if (count > 0) {
          if (marker === '[') {
            // Incomplete link - replace with plain text
            modifiedText = modifiedText.replace(/\[([^\]]*?)$/g, '$1');
          } else {
            // For other markers, we'll just append the marker to close it
            modifiedText += marker;
          }
        }
      }
      
      // Apply formatting to our fixed text
      return this.applyFormatting(modifiedText);
    }
    
    // Check for incomplete markdown markers in text
    private hasIncompleteMarkers(text: string): boolean {
      // Reset the tag counter
      for (const marker of this.openTags.keys()) {
        this.openTags.set(marker, 0);
      }
      
      // Check each marker type
      this.countMarkers(text, '**');
      this.countMarkers(text, '*');
      this.countMarkers(text, '***');
      this.countMarkers(text, '__');
      this.countMarkers(text, '`');
      this.countMarkers(text, '#');
      this.countMarkers(text, '##');
      this.countMarkers(text, '###');
      
      // Check for unmatched [ and ] for links
      this.countBrackets(text);
      
      // Check if any tag counter is odd (meaning incomplete)
      for (const [, count] of this.openTags.entries()) {
        if (count % 2 !== 0) {
          return true;
        }
      }
      
      // Also check for incomplete links specifically
      if (text.match(/\[([^\]]*?)$/)) {
        return true;
      }
      
      return false;
    }
    
    // Count occurrences of a marker in text
    private countMarkers(text: string, marker: string): void {
      const count = (text.match(new RegExp(this.escapeRegExp(marker), 'g')) || []).length;
      this.openTags.set(marker, count);
    }
    
    // Count brackets specifically for links
    private countBrackets(text: string): void {
      const openBrackets = (text.match(/\[/g) || []).length;
      const closeBrackets = (text.match(/\]/g) || []).length;
      
      // For links, we care about the difference rather than the actual count
      this.openTags.set('[', openBrackets - closeBrackets);
    }
    
    // Escape special regex characters
    private escapeRegExp(string: string): string {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
    
    // Apply formatting to text with complete markdown
    private applyFormatting(text: string): string {
      let formattedText = text;
      
      // Format bold italic first (since it has *** which could be confused with ** and *)
      formattedText = formattedText.replace(
        this.regexPatterns.boldItalic, 
        '<span class="font-bold italic">$1</span>'
      );
      
      // Format bold
      formattedText = formattedText.replace(
        this.regexPatterns.bold, 
        '<span class="font-bold">$1</span>'
      );
      
      // Format italic (but not if already part of bold)
      formattedText = formattedText.replace(
        this.regexPatterns.italic, 
        '<span class="italic">$1</span>'
      );
      
      // Format underline
      formattedText = formattedText.replace(
        this.regexPatterns.underline,
        '<span class="underline">$1</span>'
      );
      
      // Format inline code
      formattedText = formattedText.replace(
        this.regexPatterns.inlineCode,
        '<code>$1</code>'
      );
      
      // Format links - handle specially
      formattedText = formattedText.replace(
        this.regexPatterns.link,
        '<a href="$2" target="_blank" class="text-sky-500 underline text-wrap truncate"><span>$1</span></a>'
      );
      
      // Format headings - note the space after # characters
      formattedText = formattedText.replace(
        this.regexPatterns.heading1,
        '<span class="font-bold text-2xl">$1</span>\n'
      );
      
      formattedText = formattedText.replace(
        this.regexPatterns.heading2,
        '<span class="font-bold text-xl">$1</span>\n'
      );
      
      formattedText = formattedText.replace(
        this.regexPatterns.heading3,
        '<span class="font-bold text-lg">$1</span>\n'
      );
      
      // Convert newlines to breaks at the end
      formattedText = formattedText.replace(/\n/g, "<br />");
      
      return formattedText;
    }
}